import { z } from 'zod'

import { parseJsonText } from '../../domain/parseJsonText'
import { type AgentPanelSize, VIEWPORT_MARGIN, WIDGET_BOTTOM, WIDGET_RIGHT } from './agentWidgetLayout'

// パネルの伸縮（仕様の「パネルの伸縮」）。右下に固定したパネルの左の辺（横）、上の辺（縦）、左上の角（斜め）をつかんで大きさを変える。
// 変えた大きさは AgentWidget の state に持ち、再読み込み後も残すよう、ブラウザごとの設定として localStorage に覚える

// つかむ場所。left：左の辺（横だけ）、top：上の辺（縦だけ）、corner：左上の角（両方）
export type PanelResizeEdge = 'left' | 'top' | 'corner'

// パネルの最小の大きさ（px。仮）。幅は compact の枠と同じにし、高さは見出し、会話の数行、入力欄が収まる値にする
export const MIN_PANEL_SIZE: AgentPanelSize = { width: 360, height: 320 }

// 組織によらないブラウザごとの設定なので、キーに組織 ID は含めない
const STORAGE_KEY = 'salescore:agentWidget:panelSize'

// 覚えていた値の形。ブラウザに残る値は、古い版や手で書き換えた値かもしれない外部の入力なので、形を確かめてから使う
// zod 4 の z.number() は、NaN と Infinity を受け付けない
const savedPanelSizeSchema = z.object({ width: z.number(), height: z.number() })

/**
 * 覚えていた値をパネルの大きさとして読む。覚えていないときと、形が違うときは null（既定の大きさで出す）。
 */
export const parsePanelSize = (saved: string | null): AgentPanelSize | null =>
  saved === null ? null : parseJsonText(saved, savedPanelSizeSchema)

// 読めない環境（プライベートブラウズなど）では、既定の大きさで出す
export const loadPanelSize = (): AgentPanelSize | null => {
  try {
    return parsePanelSize(localStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

export const savePanelSize = (size: AgentPanelSize): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(size))
  } catch {
    // 覚えられなくても、次に開いたときに既定の大きさに戻るだけなので続ける
  }
}

const clamp = (value: number, min: number, max: number): number => Math.round(Math.min(Math.max(value, min), max))

/**
 * ドラッグの始めからのポインターの動きを、パネルの大きさに映す。左の辺は横だけ、上の辺は縦だけ、左上の角は両方を変える。
 * 右下に固定なので、左・上へ動かすと大きくなる。最小より小さくせず、画面からはみ出す大きさにもしない。
 */
export const resizedPanelSize = ({
  edge,
  start,
  startPointer,
  pointer,
  viewport,
}: {
  edge: PanelResizeEdge
  // ドラッグを始めたときのパネルの大きさとポインターの位置
  start: AgentPanelSize
  startPointer: { x: number; y: number }
  pointer: { x: number; y: number }
  viewport: { width: number; height: number }
}): AgentPanelSize => {
  const max = {
    width: Math.max(MIN_PANEL_SIZE.width, viewport.width - WIDGET_RIGHT - VIEWPORT_MARGIN),
    height: Math.max(MIN_PANEL_SIZE.height, viewport.height - WIDGET_BOTTOM - VIEWPORT_MARGIN),
  }
  return {
    width:
      edge === 'top' ? start.width : clamp(start.width + (startPointer.x - pointer.x), MIN_PANEL_SIZE.width, max.width),
    height:
      edge === 'left'
        ? start.height
        : clamp(start.height + (startPointer.y - pointer.y), MIN_PANEL_SIZE.height, max.height),
  }
}
